import {
  Button,
  CloseButton,
  Collapse,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Text,
  useDisclosure,
  VisuallyHidden,
} from '@chakra-ui/react';
import { ErrorMessage } from '@hookform/error-message';
import type { SnapSiteConfigEditFormDataUnvalidated } from '@snap-types/models/snap-site-config';
import type { FieldPathFn } from '@ui/components/form';
import { CircleInfoIcon } from '@ui/components/Icons';
import { ArrayActions } from '@ui/features/snap-site-config/screens/edit/components/ArrayActions';
import { FormPanel } from '@ui/features/snap-site-config/screens/edit/components/FormPanel';
import { FormPanelHeading } from '@ui/features/snap-site-config/screens/edit/components/FormPanelHeading';
import { FormRowActions } from '@ui/features/snap-site-config/screens/edit/components/FormRowActions';
import {
  type FieldArrayPath,
  get,
  useFieldArray,
  type UseFieldArrayReturn,
  useFormContext,
} from 'react-hook-form';

export function LastGrossMarginForm({
  fieldPath,
}: {
  fieldPath: FieldPathFn<SnapSiteConfigEditFormDataUnvalidated>;
}) {
  const form = useFormContext<SnapSiteConfigEditFormDataUnvalidated>();

  const defaultAdjustmentFieldPath = fieldPath('defaultAdjustment');

  const lastGrossMarginAdjustmentsFieldPath = fieldPath(
    'lastGrossMarginAdjustments',
  );
  const lastGrossMarginAdjustments = useFieldArray({
    name: lastGrossMarginAdjustmentsFieldPath as FieldArrayPath<SnapSiteConfigEditFormDataUnvalidated>,
    control: form.control,
  });

  return (
    <FormPanel mt={6}>
      <FormPanelHeading>Last Gross Margin Adjustments</FormPanelHeading>

      <Flex direction="column" px={4} gap={2}>
        <FormControl
          isInvalid={!!get(form.formState.errors, defaultAdjustmentFieldPath)}
        >
          <FormLabel>Default Adjustment:</FormLabel>

          <Input
            {...form.register(defaultAdjustmentFieldPath)}
            id={defaultAdjustmentFieldPath}
            type="number"
            inputMode="decimal"
            step="0.01"
            placeholder="Enter default adjustment"
            min={0}
          />

          <ErrorMessage
            name={defaultAdjustmentFieldPath}
            render={({ message }) => (
              <FormErrorMessage>{message}</FormErrorMessage>
            )}
          />
        </FormControl>

        {lastGrossMarginAdjustments.fields.length === 0 ? <EmptyState /> : null}

        {lastGrossMarginAdjustments.fields.map((item, index) => (
          <FormRow
            key={item.id}
            fieldPath={(fieldName) =>
              fieldPath(`lastGrossMarginAdjustments.${index}.${fieldName}`)
            }
            fieldActions={lastGrossMarginAdjustments}
            index={index}
          />
        ))}

        <FormRowActions>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              lastGrossMarginAdjustments.append({
                /**
                 * @NOTE(shawk): We want to force the user to pick an appropriate
                 * gross margin so we intentionally set the initial value as
                 * nothing (empty string) which we know is invalid. TS is 😡
                 */
                margin: '' as unknown as number,
                adjustment: '',
              });
            }}
          >
            Add Adjustment
          </Button>
        </FormRowActions>
      </Flex>
    </FormPanel>
  );
}

function EmptyState() {
  const { isOpen, onClose, onToggle } = useDisclosure();

  return (
    <Flex direction="column">
      <Flex
        align="center"
        gap={2}
        p={4}
        rounded="sm"
        bg="snap.teal.50"
        flexWrap="wrap"
      >
        <CircleInfoIcon color="snap.teal.400" />
        <Text fontWeight="bold" fontSize="14px" color="snap.teal.500">
          There are no adjustments set up.
        </Text>

        <Text fontSize="14px">Add an adjustment to get started.</Text>

        <Button
          variant="outline"
          size="xs"
          colorScheme="teal"
          onClick={onToggle}
        >
          What is a Last Gross Margin Adjustment?
        </Button>
      </Flex>

      <Collapse in={isOpen} animateOpacity>
        <Flex p={4} bg="mw.panelGrey" mt={4} gap={2} rounded="sm">
          <Text>
            Last Gross Margin Adjustments allow you to apply specific price
            adjustments based on the gross margin of the last transaction. This
            is useful for tailoring prices to different customer segments based
            on their transaction history.
          </Text>

          <CloseButton onClick={onClose} />
        </Flex>
      </Collapse>
    </Flex>
  );
}

function FormRow({
  fieldPath,
  fieldActions,
  index,
}: {
  fieldPath: FieldPathFn<SnapSiteConfigEditFormDataUnvalidated>;
  fieldActions: UseFieldArrayReturn<
    SnapSiteConfigEditFormDataUnvalidated,
    `priceStrategies.${number}.priceSegmentConfigs.${number}.lastGrossMarginAdjustments`
  >;
  index: number;
}) {
  const form = useFormContext();

  const marginFieldPath = fieldPath('margin');
  const adjustmentFieldPath = fieldPath('adjustment');

  return (
    <Flex gap={2} align="flex-start">
      <FormControl isInvalid={!!get(form.formState.errors, marginFieldPath)}>
        <VisuallyHidden>
          <FormLabel htmlFor={marginFieldPath}>Margin:</FormLabel>
        </VisuallyHidden>

        <Input
          {...form.register(marginFieldPath)}
          id={marginFieldPath}
          type="number"
          inputMode="decimal"
          placeholder="Enter margin"
          min={1}
        />

        <ErrorMessage
          errors={form.formState.errors}
          name={marginFieldPath}
          render={({ message }) => (
            <FormErrorMessage>{message}</FormErrorMessage>
          )}
        />
      </FormControl>

      <FormControl
        isInvalid={!!get(form.formState.errors, adjustmentFieldPath)}
      >
        <VisuallyHidden>
          <FormLabel htmlFor={adjustmentFieldPath}>Adjustment:</FormLabel>
        </VisuallyHidden>

        <Input
          {...form.register(adjustmentFieldPath)}
          id={adjustmentFieldPath}
          type="number"
          inputMode="decimal"
          placeholder="Enter adjustment"
          step="0.01"
          min={0}
        />

        <ErrorMessage
          errors={form.formState.errors}
          name={adjustmentFieldPath}
          render={({ message }) => (
            <FormErrorMessage>{message}</FormErrorMessage>
          )}
        />
      </FormControl>

      <Flex minH="40px" alignItems="center">
        <ArrayActions
          fieldActions={fieldActions}
          index={index}
          layout="horizontal"
        />
      </Flex>
    </Flex>
  );
}
